import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT_LAYOUT, UI_CENTER_X, UI_CENTER_Y } from '../core/Config';
import { DEBUG_LOG_FILE_PATH } from '../core/FileDebugLog';
import { GameSettings } from '../core/GameSettings';
import { clearPendingCrashReport, getPendingCrashReport } from '../core/RuntimeMonitor';
import {
  isTouchControlsPreferred,
  pulseVirtualActions,
  releaseVirtualDirections,
  resetVirtualControls,
  setVirtualHeld,
  triggerVirtualHaptic,
  type VirtualAction,
} from '../core/VirtualControls';
import { ActionPanel } from '../ui/ActionPanel';
import { ChoiceUI } from '../ui/ChoiceUI';
import { DialogueUI } from '../ui/DialogueUI';
import { PortraitCatalogPanel } from '../ui/PortraitCatalogPanel';
import { QuestLogPanel } from '../ui/QuestLogPanel';
import { SettingsPanel } from '../ui/SettingsPanel';
import { TeleportMenu } from '../ui/TeleportMenu';
import { WorldEditorPanel } from '../ui/WorldEditorPanel';
import { QuestSystem } from '../systems/QuestSystem';

type ShortcutRow = { label: string; value: string };

const DESKTOP_SHORTCUT_ROWS: ShortcutRow[] = [
  { label: '이동', value: 'WASD / 방향키' },
  { label: '상호작용', value: 'F / Space / Enter' },
  { label: '확인', value: 'Space / Enter' },
  { label: '닫기', value: 'Esc' },
  { label: '마을 이동', value: 'M' },
  { label: '퀘스트', value: 'Q' },
  { label: '설정', value: 'O' },
  { label: '초상화', value: 'I' },
  { label: '월드 편집', value: 'P' },
  { label: '새로고침', value: 'R' },
  { label: '디버그', value: 'T' },
  { label: '개발 모드', value: 'Alt + F1' },
  { label: '그리드 / 스냅', value: 'G / [ ]' },
];

const MOBILE_GUIDE_ROWS: ShortcutRow[] = [
  { label: '이동', value: '설정한 쪽의 8방향 조이스틱' },
  { label: '대화 · 입장', value: '반대쪽 A 버튼' },
  { label: '대화 넘기기', value: '대화창 또는 선택지 터치' },
  { label: '메뉴 닫기', value: 'B 버튼' },
  { label: '마을 이동', value: '상단 마을 버튼' },
  { label: '퀘스트', value: '상단 퀘스트 버튼' },
  { label: '게임 설정', value: '상단 설정 버튼' },
];

export class UIScene extends Phaser.Scene {
  dialogueUI!: DialogueUI;
  choiceUI!: ChoiceUI;
  actionPanel!: ActionPanel;
  teleportMenu!: TeleportMenu;
  settingsPanel!: SettingsPanel;
  questLogPanel!: QuestLogPanel;
  worldEditorPanel!: WorldEditorPanel;
  portraitCatalogPanel!: PortraitCatalogPanel;
  private shortcutPanel!: Phaser.GameObjects.Container;
  private shortcutToggleButton?: Phaser.GameObjects.Container;
  private mobileToolbar?: Phaser.GameObjects.Container;
  private mobileDpad?: Phaser.GameObjects.Container;
  private mobileConfirmButton?: Phaser.GameObjects.Container;
  private mobileCancelButton?: Phaser.GameObjects.Container;
  private mobileConfirmCaption?: Phaser.GameObjects.Text;
  private crashPanel?: Phaser.GameObjects.Container;
  private crashPanelVisible = false;
  private readonly touchControlsEnabled = isTouchControlsPreferred() || IS_PORTRAIT_LAYOUT;
  private shortcutPanelVisible = !this.touchControlsEnabled;
  private actionContext = '확인';
  private mobileControlSignature = '';

  constructor() {
    super('UIScene');
  }

  create() {
    resetVirtualControls();

    this.dialogueUI = new DialogueUI(this);
    this.choiceUI = new ChoiceUI(this);
    this.actionPanel = new ActionPanel(this);
    this.teleportMenu = new TeleportMenu(this);
    this.settingsPanel = new SettingsPanel(this);
    this.questLogPanel = new QuestLogPanel(this);
    this.worldEditorPanel = new WorldEditorPanel(this);
    this.portraitCatalogPanel = new PortraitCatalogPanel(this);

    const portraits = (this.registry.get('portraitManifest') ?? []) as any[];
    this.portraitCatalogPanel.setItems(portraits);

    this.createShortcutPanel();
    if (this.touchControlsEnabled) {
      this.createMobileToolbar();
      this.createMobileControls();
    } else {
      this.createDesktopShortcutToggle();
    }
    this.createCrashReportPanel();
    const unsubscribeQuest = QuestSystem.subscribe((update) => {
      this.questLogPanel.render();
      this.actionPanel.flash([update.message]);
    });

    this.events.on('set-location', () => {});
    this.events.on('set-source', () => {});
    this.events.on('refresh-settings-ui', () => this.applySettingsVisibility());

    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.crashPanelVisible) {
        this.hideCrashReportPanel();
      } else if (this.shortcutPanelVisible) {
        this.toggleShortcutPanel();
      }
    });
    this.input.keyboard?.on('keydown-H', () => {
      if (!this.crashPanelVisible) this.toggleShortcutPanel();
    });

    this.input.keyboard?.on('keydown-C', async () => {
      if (!this.crashPanelVisible || !this.crashPanel) return;
      const report = getPendingCrashReport();
      if (!report) return;
      const pendingSceneLines = report.pendingSceneInit
        ? [
            `pendingScene=${report.pendingSceneInit.scene}`,
            `pendingSceneStartedAt=${report.pendingSceneInit.startedAt}`,
            `pendingSceneData=${JSON.stringify(report.pendingSceneInit.data ?? null)}`,
          ]
        : [];
      const text = [
        `startedAt=${report.startedAt}`,
        `lastHeartbeatAt=${report.lastHeartbeatAt}`,
        `debugLogFilePath=${DEBUG_LOG_FILE_PATH}`,
        ...pendingSceneLines,
        ...report.recentLogs,
      ].join('\n');
      try {
        await navigator.clipboard.writeText(text);
      } catch {}
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      unsubscribeQuest();
      resetVirtualControls();
    });
    this.applySettingsVisibility();
    this.showDevelopmentPreview();
  }

  update() {
    this.applySettingsVisibility();
  }

  setActionContext(label?: string) {
    const next = label?.trim() || '확인';
    if (next === this.actionContext) return;
    this.actionContext = next;
    this.refreshMobileActionLabels();
  }

  toggleTeleportMenu(currentVillageId?: string) {
    const shouldOpen = !this.teleportMenu.isVisible();
    this.closePanels();
    if (shouldOpen) this.teleportMenu.toggle(currentVillageId);
    this.applySettingsVisibility();
  }

  toggleSettingsPanel() {
    const shouldOpen = !this.settingsPanel.isVisible();
    this.closePanels();
    if (shouldOpen) this.settingsPanel.toggle();
    this.applySettingsVisibility();
  }

  toggleQuestLogPanel() {
    const shouldOpen = !this.questLogPanel.isVisible();
    this.closePanels();
    if (shouldOpen) this.questLogPanel.toggle();
    this.applySettingsVisibility();
  }

  togglePortraitCatalog() {
    const shouldOpen = !this.portraitCatalogPanel.isVisible();
    this.closePanels();
    if (shouldOpen) this.portraitCatalogPanel.toggle();
    this.applySettingsVisibility();
  }

  toggleWorldEditorPanel() {
    const shouldOpen = !this.worldEditorPanel.isVisible();
    this.closePanels();
    if (shouldOpen) this.worldEditorPanel.toggle();
    this.applySettingsVisibility();
  }

  closePanels() {
    this.teleportMenu.hide();
    this.settingsPanel.hide();
    this.questLogPanel.hide();
    this.portraitCatalogPanel.hide();
    this.worldEditorPanel.setVisible(false);
    this.applySettingsVisibility();
  }

  private hasModalOpen() {
    return (
      this.teleportMenu.isVisible() ||
      this.settingsPanel.isVisible() ||
      this.questLogPanel.isVisible() ||
      this.portraitCatalogPanel.isVisible() ||
      this.worldEditorPanel.isVisible()
    );
  }

  private createShortcutPanel() {
    if (this.touchControlsEnabled) {
      this.createMobileGuide();
      return;
    }

    const panelWidth = 560;
    const rowHeight = 38;
    const rowGap = 6;
    const rowCount = Math.ceil(DESKTOP_SHORTCUT_ROWS.length / 2);
    const panelHeight = 78 + rowCount * (rowHeight + rowGap) + 34;
    const panelX = GAME_WIDTH - panelWidth / 2 - 24;
    const panelY = GAME_HEIGHT - panelHeight / 2 - 20;
    const outer = this.add.rectangle(panelX, panelY, panelWidth, panelHeight, 0x172019, 0.96).setStrokeStyle(2, 0x8ebc98, 0.92);
    const inner = this.add.rectangle(panelX, panelY, panelWidth - 16, panelHeight - 16, 0x0c120f, 0.98).setStrokeStyle(1, 0xe8c768, 0.55);
    const title = this.add.text(panelX - panelWidth / 2 + 18, panelY - panelHeight / 2 + 12, '조작 안내', {
      fontSize: '20px',
      color: '#fff0b5',
      fontStyle: 'bold',
    });
    const children: Phaser.GameObjects.GameObject[] = [outer, inner, title];
    const columnGap = 12;
    const columnWidth = (panelWidth - 48 - columnGap) / 2;
    const topY = panelY - panelHeight / 2 + 48;
    const leftX = panelX - panelWidth / 2 + 18;
    const rightX = leftX + columnWidth + columnGap;

    DESKTOP_SHORTCUT_ROWS.forEach((row, index) => {
      const columnIndex = index % 2;
      const rowIndex = Math.floor(index / 2);
      const baseX = columnIndex === 0 ? leftX : rightX;
      const baseY = topY + rowIndex * (rowHeight + rowGap);
      const rowBg = this.add.rectangle(baseX + columnWidth / 2, baseY + rowHeight / 2, columnWidth, rowHeight, 0x233129, 0.96).setStrokeStyle(1, 0x6f8d75, 0.7);
      const label = this.add.text(baseX + 10, baseY + 9, row.label, {
        fontSize: '15px',
        color: '#ffe391',
        fontStyle: 'bold',
      });
      const value = this.add.text(baseX + columnWidth - 10, baseY + 9, row.value, {
        fontSize: '15px',
        color: '#dfe9dc',
      }).setOrigin(1, 0);
      children.push(rowBg, label, value);
    });

    const footer = this.add.text(panelX - panelWidth / 2 + 18, panelY + panelHeight / 2 - 26, '빠른 이동: NumPad 1-9   H: 안내 토글', {
      fontSize: '13px',
      color: '#9eb5a1',
    });
    children.push(footer);
    this.shortcutPanel = this.add.container(0, 0, children).setDepth(10990).setScrollFactor(0);
  }

  private createMobileGuide() {
    const panelWidth = GAME_WIDTH - 56;
    const panelHeight = 900;
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x050806, 0.68).setOrigin(0);
    const shadow = this.add.rectangle(UI_CENTER_X + 8, UI_CENTER_Y + 10, panelWidth, panelHeight, 0x000000, 0.35);
    const outer = this.add.rectangle(UI_CENTER_X, UI_CENTER_Y, panelWidth, panelHeight, 0x172019, 0.99).setStrokeStyle(5, 0xe8c768, 0.96);
    const inner = this.add.rectangle(UI_CENTER_X, UI_CENTER_Y, panelWidth - 28, panelHeight - 28, 0x0c120f, 0.99).setStrokeStyle(2, 0x8ebc98, 0.72);
    const title = this.add.text(UI_CENTER_X, UI_CENTER_Y - 380, '모바일 조작', {
      fontSize: '44px',
      color: '#fff0b5',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    const subtitle = this.add.text(UI_CENTER_X, UI_CENTER_Y - 324, '설정에서 크기 · 투명도 · 좌우 배치를 바꿀 수 있습니다', {
      fontSize: '23px',
      color: '#b9ddbf',
    }).setOrigin(0.5);
    const children: Phaser.GameObjects.GameObject[] = [dim, shadow, outer, inner, title, subtitle];
    const rowWidth = panelWidth - 78;

    MOBILE_GUIDE_ROWS.forEach((row, index) => {
      const y = UI_CENTER_Y - 244 + index * 82;
      const bg = this.add.rectangle(UI_CENTER_X, y, rowWidth, 72, 0x233129, 0.9).setStrokeStyle(2, 0x6f8d75, 0.56);
      const label = this.add.text(UI_CENTER_X - rowWidth / 2 + 28, y - 22, row.label, {
        fontSize: '29px',
        color: '#ffe391',
        fontStyle: 'bold',
      });
      const value = this.add.text(UI_CENTER_X + rowWidth / 2 - 28, y - 21, row.value, {
        fontSize: '27px',
        color: '#eef4ec',
      }).setOrigin(1, 0);
      children.push(bg, label, value);
    });

    const closeBg = this.add
      .rectangle(UI_CENTER_X, UI_CENTER_Y + 376, 300, 78, 0x4d684f, 0.98)
      .setStrokeStyle(3, 0xe8c768, 0.86)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.toggleShortcutPanel());
    const closeText = this.add.text(UI_CENTER_X, UI_CENTER_Y + 376, '확인', {
      fontSize: '32px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    children.push(closeBg, closeText);
    this.shortcutPanel = this.add.container(0, 0, children).setDepth(12200).setScrollFactor(0);
  }

  private createDesktopShortcutToggle() {
    const width = 136;
    const height = 42;
    const x = GAME_WIDTH - width / 2 - 18;
    const y = 28 + height / 2;
    const bg = this.add
      .rectangle(x, y, width, height, 0x172019, 0.92)
      .setStrokeStyle(1, 0x8ebc98, 0.8)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.toggleShortcutPanel());
    const label = this.add.text(x, y, '', {
      fontSize: '16px',
      color: '#eef4ec',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.shortcutToggleButton = this.add.container(0, 0, [bg, label]).setDepth(12020).setScrollFactor(0);
    this.shortcutToggleButton.setData('label', label);
    this.refreshShortcutToggleLabel();
  }

  private createMobileToolbar() {
    const y = 66;
    const bar = this.add
      .rectangle(UI_CENTER_X, y, GAME_WIDTH - 48, 88, 0x0c120f, 0.78)
      .setStrokeStyle(2, 0x6f8d75, 0.68);
    const buttons = [
      this.createToolbarButton(135, y, 190, '마을', () => this.toggleTeleportMenu()),
      this.createToolbarButton(345, y, 190, '퀘스트', () => this.toggleQuestLogPanel()),
      this.createToolbarButton(555, y, 190, '설정', () => this.toggleSettingsPanel()),
      this.createToolbarButton(765, y, 190, '조작', () => this.toggleShortcutPanel()),
    ];
    this.mobileToolbar = this.add.container(0, 0, [bar, ...buttons]).setDepth(12030).setScrollFactor(0);
  }

  private createToolbarButton(x: number, y: number, width: number, label: string, onPress: () => void) {
    const bg = this.add
      .rectangle(x, y, width, 68, 0x2d3d31, 0.98)
      .setStrokeStyle(2, 0x8ebc98, 0.72)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        bg.setFillStyle(0x4d684f, 1);
        this.time.delayedCall(100, () => bg.setFillStyle(0x2d3d31, 0.98));
        onPress();
      });
    const text = this.add.text(x, y, label, {
      fontSize: '28px',
      color: '#f3f6ef',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    return this.add.container(0, 0, [bg, text]);
  }

  private toggleShortcutPanel() {
    const shouldOpen = !this.shortcutPanelVisible;
    if (shouldOpen) this.closePanels();
    this.shortcutPanelVisible = shouldOpen;
    this.refreshShortcutToggleLabel();
    this.applySettingsVisibility();
  }

  private refreshShortcutToggleLabel() {
    const label = this.shortcutToggleButton?.getData('label') as Phaser.GameObjects.Text | undefined;
    label?.setText(this.shortcutPanelVisible ? '안내 닫기' : '조작 안내');
  }

  private createMobileControls() {
    this.mobileDpad = this.createVirtualStick(206, GAME_HEIGHT - 205, 168);

    const confirm = this.createTapButton(790, GAME_HEIGHT - 166, 150, 'A', '확인', ['confirm', 'interact'], 0x49694f, 0xb9ddbf);
    this.mobileConfirmButton = confirm.container;
    this.mobileConfirmCaption = confirm.caption;
    const cancel = this.createTapButton(610, GAME_HEIGHT - 132, 118, 'B', '닫기', ['cancel'], 0x6b3f3b, 0xe6a59d);
    this.mobileCancelButton = cancel.container;
    this.refreshMobileActionLabels();
  }

  private createVirtualStick(x: number, y: number, radius: number) {
    let stickContainer: Phaser.GameObjects.Container;
    const shadow = this.add.circle(7, 9, radius, 0x000000, 0.28);
    const base = this.add
      .circle(0, 0, radius, 0x172019, 0.62)
      .setStrokeStyle(5, 0xb9ddbf, 0.62)
      .setInteractive({ useHandCursor: false });
    const ring = this.add.circle(0, 0, radius - 25, 0x2d3d31, 0.36).setStrokeStyle(2, 0x8ebc98, 0.42);
    const thumb = this.add.circle(0, 0, 62, 0x4d684f, 0.9).setStrokeStyle(4, 0xe8c768, 0.72);
    const arrows = [
      this.add.text(0, -radius + 28, '▲', { fontSize: '32px', color: '#dce9da' }).setOrigin(0.5),
      this.add.text(0, radius - 28, '▼', { fontSize: '32px', color: '#dce9da' }).setOrigin(0.5),
      this.add.text(-radius + 28, 0, '◀', { fontSize: '32px', color: '#dce9da' }).setOrigin(0.5),
      this.add.text(radius - 28, 0, '▶', { fontSize: '32px', color: '#dce9da' }).setOrigin(0.5),
    ];
    const updateStick = (pointer: Phaser.Input.Pointer) => {
      const dx = (pointer.x - (stickContainer?.x ?? x)) / (stickContainer?.scaleX || 1);
      const dy = (pointer.y - (stickContainer?.y ?? y)) / (stickContainer?.scaleY || 1);
      const distance = Math.max(1, Math.sqrt(dx * dx + dy * dy));
      const limit = radius - 70;
      const scale = Math.min(1, limit / distance);
      thumb.setPosition(dx * scale, dy * scale);
      const deadZone = 28;
      setVirtualHeld('left', dx < -deadZone);
      setVirtualHeld('right', dx > deadZone);
      setVirtualHeld('up', dy < -deadZone);
      setVirtualHeld('down', dy > deadZone);
    };
    const resetStick = () => {
      thumb.setPosition(0, 0);
      releaseVirtualDirections();
    };
    base.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      triggerVirtualHaptic(10);
      updateStick(pointer);
    });
    base.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) updateStick(pointer);
    });
    base.on('pointerup', resetStick);
    base.on('pointerupoutside', resetStick);
    this.input.on('pointerup', resetStick);
    stickContainer = this.add
      .container(0, 0, [shadow, base, ring, ...arrows, thumb])
      .setPosition(x, y)
      .setDepth(12000)
      .setScrollFactor(0);
    return stickContainer;
  }

  private createTapButton(
    x: number,
    y: number,
    size: number,
    label: string,
    captionText: string,
    actions: VirtualAction[],
    color: number,
    strokeColor: number,
  ) {
    const shadow = this.add.circle(6, 8, size / 2, 0x000000, 0.3);
    const bg = this.add
      .circle(0, 0, size / 2, color, 0.86)
      .setStrokeStyle(4, strokeColor, 0.82)
      .setInteractive({ useHandCursor: false });
    const text = this.add.text(0, -5, label, {
      fontSize: `${Math.round(size * 0.34)}px`,
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    const caption = this.add.text(0, size / 2 + 14, captionText, {
      fontSize: '25px',
      color: '#f3f6ef',
      fontStyle: 'bold',
      backgroundColor: '#0c120fbb',
      padding: { x: 9, y: 4 },
    }).setOrigin(0.5, 0);
    bg.on('pointerdown', () => {
      pulseVirtualActions(actions);
      bg.setFillStyle(color, 1);
      text.setScale(0.92);
      this.time.delayedCall(100, () => {
        bg.setFillStyle(color, 0.86);
        text.setScale(1);
      });
    });
    return {
      container: this.add.container(x, y, [shadow, bg, text, caption]).setDepth(12500).setScrollFactor(0),
      caption,
    };
  }

  private refreshMobileActionLabels() {
    if (!this.mobileConfirmCaption) return;
    if (this.dialogueUI?.isVisible()) {
      this.mobileConfirmCaption.setText(this.choiceUI?.isVisible() ? '선택' : '다음');
    } else if (this.hasModalOpen()) {
      this.mobileConfirmCaption.setText('선택');
    } else {
      this.mobileConfirmCaption.setText(this.actionContext);
    }
  }

  private createCrashReportPanel() {
    const report = getPendingCrashReport();
    if (!report) return;
    const recentLines = report.recentLogs.length > 0 ? report.recentLogs : ['로그가 없습니다.'];
    const pendingSceneLines = report.pendingSceneInit
      ? [
          `미완료 씬: ${report.pendingSceneInit.scene}`,
          `씬 시작 시각: ${report.pendingSceneInit.startedAt}`,
          `씬 데이터: ${JSON.stringify(report.pendingSceneInit.data ?? null)}`,
          '',
        ]
      : [];
    const width = Math.min(GAME_WIDTH - 80, 900);
    const height = Math.min(GAME_HEIGHT - 80, 620);
    const dim = this.add.rectangle(UI_CENTER_X, UI_CENTER_Y, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.76);
    const outer = this.add.rectangle(UI_CENTER_X, UI_CENTER_Y, width, height, 0x251313, 0.99).setStrokeStyle(4, 0xef6b62, 0.95);
    const inner = this.add.rectangle(UI_CENTER_X, UI_CENTER_Y, width - 24, height - 24, 0x0c120f, 0.99).setStrokeStyle(2, 0xe8c768, 0.55);
    const title = this.add.text(UI_CENTER_X - width / 2 + 20, UI_CENTER_Y - height / 2 + 18, '직전 실행에서 비정상 종료가 감지되었습니다.', {
      fontSize: IS_PORTRAIT_LAYOUT ? '34px' : '24px',
      color: '#ffc4be',
      fontStyle: 'bold',
      wordWrap: { width: width - 150 },
    });
    const meta = this.add.text(UI_CENTER_X - width / 2 + 20, UI_CENTER_Y - height / 2 + (IS_PORTRAIT_LAYOUT ? 104 : 70), [
      `시작 시각: ${report.startedAt}`,
      `마지막 하트비트: ${report.lastHeartbeatAt}`,
      `로그 파일: ${DEBUG_LOG_FILE_PATH}`,
      ...pendingSceneLines,
      '최근 로그',
    ], {
      fontSize: IS_PORTRAIT_LAYOUT ? '23px' : '16px',
      color: '#dfe9dc',
      lineSpacing: 7,
      wordWrap: { width: width - 40 },
    });
    const logs = this.add.text(UI_CENTER_X - width / 2 + 20, UI_CENTER_Y - height / 2 + (IS_PORTRAIT_LAYOUT ? 270 : 190), recentLines.join('\n'), {
      fontSize: IS_PORTRAIT_LAYOUT ? '21px' : '15px',
      color: '#d5e8d6',
      lineSpacing: 7,
      wordWrap: { width: width - 40 },
    });
    const closeButton = this.add
      .rectangle(UI_CENTER_X + width / 2 - (IS_PORTRAIT_LAYOUT ? 76 : 54), UI_CENTER_Y - height / 2 + (IS_PORTRAIT_LAYOUT ? 54 : 38), IS_PORTRAIT_LAYOUT ? 126 : 72, IS_PORTRAIT_LAYOUT ? 64 : 42, 0x4d684f, 1)
      .setStrokeStyle(2, 0xe8c768, 0.85)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.hideCrashReportPanel());
    const closeLabel = this.add.text(closeButton.x, closeButton.y, '닫기', {
      fontSize: IS_PORTRAIT_LAYOUT ? '28px' : '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.crashPanel = this.add
      .container(0, 0, [dim, outer, inner, title, meta, logs, closeButton, closeLabel])
      .setDepth(13000)
      .setScrollFactor(0);
    this.crashPanelVisible = true;
  }

  private showDevelopmentPreview() {
    if (!import.meta.env.DEV || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const preview = params.get('uiPreview') ?? 'hud';
    const testMode = params.get('uiTest') === '1';
    if (!params.has('uiPreview') && !testMode) return;

    this.time.delayedCall(700, () => {
      if (preview === 'settings') this.toggleSettingsPanel();
      if (preview === 'teleport') this.toggleTeleportMenu();
      if (preview === 'quest') this.toggleQuestLogPanel();
      if (preview === 'guide' && !this.shortcutPanelVisible) this.toggleShortcutPanel();
      if (preview === 'dialogue' || preview === 'choice') {
        this.dialogueUI.show(
          '마을 안내인',
          '모바일에서도 대사와 선택지를 편안하게 읽고 바로 눌러 진행할 수 있습니다.',
          'npc_village',
          true,
        );
      }
      if (preview === 'choice') {
        this.choiceUI.show(['마을을 둘러본다', '안내를 다시 듣는다', '대화를 마친다'], 0);
      }
      if (testMode) {
        if (this.scene.isActive('VillageScene')) this.scene.pause('VillageScene');
        if (this.scene.isActive('HouseScene')) this.scene.pause('HouseScene');
        document.documentElement.dataset.jrpgUiReady = preview;
      }
    });
  }

  private hideCrashReportPanel() {
    this.crashPanel?.destroy(true);
    this.crashPanel = undefined;
    this.crashPanelVisible = false;
    clearPendingCrashReport();
    this.applySettingsVisibility();
  }

  private applySettingsVisibility() {
    if (!this.shortcutPanel || !this.dialogueUI || !this.teleportMenu || !this.settingsPanel) return;
    const dialogueOpen = this.dialogueUI.isVisible();
    const modalOpen = this.hasModalOpen();
    const guideOpen = this.shortcutPanelVisible;
    const available = !this.crashPanelVisible;

    this.shortcutPanel.setVisible(available && guideOpen);
    this.shortcutToggleButton?.setVisible(available);
    this.mobileToolbar?.setVisible(available && !guideOpen && !dialogueOpen && !modalOpen);
    const showMovement = available && !guideOpen && !dialogueOpen && !modalOpen;
    this.mobileDpad?.setVisible(showMovement);
    if (!showMovement) releaseVirtualDirections();
    this.mobileConfirmButton?.setVisible(available && !guideOpen && !dialogueOpen && !modalOpen);
    this.mobileCancelButton?.setVisible(available && !guideOpen && modalOpen);
    this.applyMobileControlSettings();
    this.refreshMobileActionLabels();
  }

  private applyMobileControlSettings() {
    if (!this.mobileDpad || !this.mobileConfirmButton || !this.mobileCancelButton) return;
    const settings = GameSettings.getAll();
    const signature = [
      settings.mobileControlScale,
      settings.mobileControlOpacity,
      settings.mobileControlLayout,
    ].join(':');
    if (signature === this.mobileControlSignature) return;
    this.mobileControlSignature = signature;

    const reversed = settings.mobileControlLayout === 'reversed';
    const scale = Phaser.Math.Clamp(settings.mobileControlScale, 0.85, 1.15);
    const alpha = Phaser.Math.Clamp(settings.mobileControlOpacity, 0.55, 1);

    this.mobileDpad
      .setPosition(reversed ? GAME_WIDTH - 206 : 206, GAME_HEIGHT - 205)
      .setScale(scale)
      .setAlpha(alpha);
    this.mobileConfirmButton
      .setPosition(reversed ? 110 : 790, GAME_HEIGHT - 166)
      .setScale(scale)
      .setAlpha(alpha);
    this.mobileCancelButton
      .setPosition(reversed ? 290 : 610, GAME_HEIGHT - 132)
      .setScale(scale)
      .setAlpha(alpha);
  }
}
